// src/features/works/WorksList.jsx - CON PERSISTENCIA DE FILTROS AL NAVEGAR
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Container, Button, Card, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaBuilding, FaMapMarkerAlt, FaEye, FaEyeSlash, FaToggleOn } from 'react-icons/fa';
import dayjs from 'dayjs';

// Componentes
import TableWrapper from '../../components/TableWrapper';
import FilterSelect from '../../components/FilterSelect';
import CustomSnackbar from '../../components/CustomSnackbar';
import ExportCsvButton from '../../components/ExportCsvButton';

// Hooks personalizados
import { useColumnFilter } from '../../hooks/useColumnFilter';

// Servicios y utilidades
import WorksService from './WorksService';
import { FORMATO_FECHA } from '../../utils/constants';

// ✅ CLAVE PARA PERSISTENCIA DEL ESTADO showFinalized SOLAMENTE
const SHOW_FINALIZED_KEY = 'works-list-showFinalized';

/**
 * Componente que muestra la lista de obras - CON PERSISTENCIA DE FILTROS
 */
function WorksList() {
  // Estados
  const [works, setWorks] = useState([]);
  const [filteredWorks, setFilteredWorks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, type: '', message: '' });
  const [gridApi, setGridApi] = useState(null);

  // ✅ ESTADO CON PERSISTENCIA PARA MOSTRAR FINALIZADAS SOLAMENTE
  const [showFinalized, setShowFinalized] = useState(() => {
    try {
      const saved = sessionStorage.getItem(SHOW_FINALIZED_KEY);
      return saved ? JSON.parse(saved) : false;
    } catch (error) {
      console.error('Error al recuperar estado showFinalized:', error);
      return false;
    }
  });
  
  const navigate = useNavigate();

  // ✅ FUNCIÓN PARA NORMALIZAR TEXTO REMOVIENDO ACENTOS/TILDES
  const normalizeText = useCallback((text) => {
    if (!text || typeof text !== 'string') return '';
    return text
      .normalize('NFD') // Descompone caracteres con acentos
      .replace(/[\u0300-\u036f]/g, '') // Remueve marcas diacríticas (acentos)
      .toLowerCase()
      .trim();
  }, []);

  // ✅ FUNCIÓN PARA CARGAR OBRAS CON PERSISTENCIA DE ESTADO
  const loadWorks = useCallback(async () => {
    try {
      setLoading(true);
      const data = await WorksService.getAllWorks({ 
        showDeleted: showFinalized 
      });
      setWorks(data);
      setFilteredWorks(data);
    } catch (error) {
      console.error('Error al cargar obras:', error);
      setSnackbar({
        open: true,
        type: 'error',
        message: 'Error al cargar la lista de obras'
      });
    } finally {
      setLoading(false);
    }
  }, [showFinalized]);

  // Cargar datos al iniciar y cuando cambie showFinalized
  useEffect(() => {
    loadWorks();
  }, [loadWorks]);

  // ✅ PERSISTIR SOLO EL ESTADO showFinalized (los filtros ya se persisten automáticamente)
  useEffect(() => {
    try {
      sessionStorage.setItem(SHOW_FINALIZED_KEY, JSON.stringify(showFinalized));
    } catch (error) {
      console.error('Error al guardar estado showFinalized:', error);
    }
  }, [showFinalized]);

  // Función para extraer el valor del cliente (estable, no cambia en cada renderizado)
  const clientValueGetter = useCallback((work) => {
    if (work.customers && work.customers.length > 0) {
      return work.customers[0].businessName || '';
    }
    return '';
  }, []);

  // ✅ Función para extraer el estado de la obra (Activa/Finalizada)
  const statusValueGetter = useCallback((work) => {
    return work.deletedDate ? 'Finalizada' : 'Activa';
  }, []);

  // ✅ CONFIGURAR FILTROS CON CLAVE ÚNICA PARA PERSISTENCIA
  const { 
    filterOptions: clientOptions, 
    activeFilters: activeClients,
    handleOptionsUpdate: handleClientOptionsUpdate,
    handleFiltersChange: handleClientChange,
    filterData: filterByClient
  } = useColumnFilter(works, 'customers', clientValueGetter, 'works-list-clients');

  // Filtro para Ubicación
  const { 
    filterOptions: locationOptions, 
    activeFilters: activeLocations,
    handleOptionsUpdate: handleLocationOptionsUpdate,
    handleFiltersChange: handleLocationChange,
    filterData: filterByLocation
  } = useColumnFilter(works, 'location', null, 'works-list-locations');

  // ✅ Filtro para Estado (solo activo cuando showFinalized es true)
  const { 
    filterOptions: statusOptions, 
    activeFilters: activeStatuses,
    handleOptionsUpdate: handleStatusOptionsUpdate,
    handleFiltersChange: handleStatusChange,
    filterData: filterByStatus
  } = useColumnFilter(works, 'deletedDate', statusValueGetter, 'works-list-status');

  // Aplicar filtros cuando cambian los datos o los filtros activos
  useEffect(() => {
    if (!works || works.length === 0) return;
    
    // Comenzar con todos los datos originales
    let result = [...works];
    
    // Aplicar filtros en cadena solo si hay filtros activos
    if (activeClients && activeClients.length > 0) {
      result = filterByClient(result);
    }
    
    if (activeLocations && activeLocations.length > 0) {
      result = filterByLocation(result);
    }

    // ✅ Aplicar filtro de estado solo si showFinalized está activo
    if (showFinalized && activeStatuses && activeStatuses.length > 0) {
      result = filterByStatus(result);
    }
    
    // Actualizar el estado solo si ha cambiado el resultado
    const resultIds = result.map(item => item.id).sort().join(',');
    const currentIds = filteredWorks.map(item => item.id).sort().join(',');
    
    if (resultIds !== currentIds) {
      setFilteredWorks(result);
    }
  }, [
    works, 
    activeClients, 
    activeLocations, 
    activeStatuses,
    showFinalized,
    filterByClient, 
    filterByLocation, 
    filterByStatus,
    filteredWorks
  ]);

  // ✅ FUNCIONES PARA MANEJAR ACCIONES (sin guardar estado extra, ya se persiste automáticamente)
  const handleViewWork = useCallback((work) => {
    navigate(`/works/${work.id}`);
  }, [navigate]);

  const handleEditWork = useCallback((work) => {
    // ✅ BLOQUEAR edición de obras finalizadas
    if (work.deletedDate) {
      setSnackbar({
        open: true,
        type: 'warning',
        message: 'No se puede editar una obra finalizada'
      });
      return;
    }

    navigate(`/works/${work.id}/edit`);
  }, [navigate]);

  const handleDeleteWork = useCallback(async (work) => {
    // ✅ BLOQUEAR eliminación de obras finalizadas
    if (work.deletedDate) {
      setSnackbar({
        open: true,
        type: 'warning', 
        message: 'No se puede eliminar una obra que ya está finalizada'
      });
      return;
    }

    if (window.confirm(`¿Está seguro que desea finalizar la obra "${work.name}"?`)) {
      try {
        // ✅ USAR EL NUEVO MÉTODO finalizeWork
        const result = await WorksService.finalizeWork(work.id);
        
        // Manejar éxito (códigos 2xx)
        if (result.statusCode >= 200 && result.statusCode < 300) {
          setSnackbar({
            open: true,
            type: 'success',
            message: 'Obra finalizada correctamente'
          });
          loadWorks();
        } else {
          // Manejar errores - usar el mensaje del API directamente
          const errorMessage = result.data?.message || `Error del servidor (${result.statusCode})`;
          
          setSnackbar({
            open: true,
            type: 'error',
            message: errorMessage
          });
        }
      } catch (error) {
        // Error inesperado en el cliente
        console.error('Error inesperado al finalizar obra:', error);
        setSnackbar({
          open: true,
          type: 'error',
          message: 'Error inesperado al finalizar la obra. Intente nuevamente.'
        });
      }
    }
  }, [loadWorks]);

  // Función para crear nueva obra
  const handleAddWork = useCallback(() => {
    navigate('/works/add');
  }, [navigate]);

  // ✅ FUNCIÓN PARA TOGGLE MOSTRAR FINALIZADAS (solo persiste este estado)
  const handleToggleShowFinalized = useCallback(() => {
    setShowFinalized(prev => {
      const newValue = !prev;
      
      // ✅ LIMPIAR FILTROS DE ESTADO SI OCULTAMOS FINALIZADAS
      if (!newValue && activeStatuses && activeStatuses.length > 0) {
        handleStatusChange([]);
      }
      
      return newValue;
    });
  }, [activeStatuses, handleStatusChange]);

  // Función para cerrar snackbar
  const handleCloseSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  // ✅ MOSTRAR INDICADOR VISUAL SI HAY FILTROS APLICADOS
  const hasActiveFilters = useMemo(() => {
    const clientFilters = activeClients?.length || 0;
    const locationFilters = activeLocations?.length || 0;
    const statusFilters = showFinalized ? (activeStatuses?.length || 0) : 0;
    return clientFilters + locationFilters + statusFilters > 0;
  }, [activeClients, activeLocations, activeStatuses, showFinalized]);

  // ✅ CONFIGURACIÓN MEJORADA PARA FILTROS DE TEXTO SIN ACENTOS
  const textFilterParams = useMemo(() => ({
    debounceMs: 200,
    suppressAndOrCondition: true,
    textMatcher: ({ filterOption, value, filterText }) => {
      if (!filterText) return true;
      
      const normalizedValue = normalizeText(value);
      const normalizedFilter = normalizeText(filterText);
      
      switch (filterOption) {
        case 'contains':
          return normalizedValue.includes(normalizedFilter);
        case 'notContains':
          return !normalizedValue.includes(normalizedFilter);
        case 'equals':
          return normalizedValue === normalizedFilter;
        case 'notEqual':
          return normalizedValue !== normalizedFilter;
        case 'startsWith':
          return normalizedValue.startsWith(normalizedFilter);
        case 'endsWith':
          return normalizedValue.endsWith(normalizedFilter);
        default:
          return normalizedValue.includes(normalizedFilter);
      }
    }
  }), [normalizeText]);

  // ✅ CONFIGURACIÓN ESPECÍFICA PARA CAMPOS DE FECHA
  const dateFilterParams = useMemo(() => ({
    debounceMs: 200,
    browserDatePicker: true
  }), []);

  // ✅ DEFINICIÓN DE COLUMNAS
  const columnDefs = useMemo(() => [
    { 
      headerName: 'Estado', 
      field: 'deletedDate', 
      width: 90,
      cellRenderer: params => {
        const isFinalized = params.value !== null;
        return (
          <Badge bg={isFinalized ? 'secondary' : 'success'} className="w-100">
            {isFinalized ? 'Finalizada' : 'Activa'}
          </Badge>
        );
      },
      filter: false,
      floatingFilter: false,
      filterValueGetter: params => {
        return params.data.deletedDate !== null ? 'Finalizada' : 'Activa';
      }
    },
    { 
      headerName: 'Nombre', 
      field: 'name', 
      flex: 1.5,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: textFilterParams
    },
    { 
      headerName: 'Ubicación', 
      field: 'location', 
      flex: 1,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: textFilterParams
    },
    { 
      headerName: 'Fecha Inicio', 
      field: 'startDate', 
      width: 130,
      valueFormatter: params => params.value ? dayjs(params.value).format(FORMATO_FECHA) : '',
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterValueGetter: params => {
        return params.data.startDate ? dayjs(params.data.startDate).format(FORMATO_FECHA) : '';
      },
      filterParams: dateFilterParams
    },
    { 
      headerName: 'Fecha Finalización', 
      field: 'deletedDate', 
      width: 150,
      valueFormatter: params => {
        return params.value ? dayjs(params.value).format(FORMATO_FECHA) : '';
      },
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterValueGetter: params => {
        return params.data.deletedDate ? dayjs(params.data.deletedDate).format(FORMATO_FECHA) : '';
      },
      filterParams: dateFilterParams,
      hide: !showFinalized && !works.some(work => work.deletedDate !== null)
    },
    { 
      headerName: 'Cliente', 
      field: 'customers',
      flex: 1,
      valueGetter: params => {
        const customers = params.data.customers;
        return customers && customers.length > 0 ? customers[0].businessName : '';
      },
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: textFilterParams
    },
    { 
      headerName: 'Observaciones', 
      field: 'observations', 
      flex: 2,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: textFilterParams
    }
  ], [textFilterParams, dateFilterParams, showFinalized, works]);

  // Estilo personalizado para forzar esquinas redondeadas
  const customTableStyle = {
    borderRadius: '8px',
    overflow: 'hidden'
  };

  // ✅ CONTAR OBRAS ACTIVAS Y FINALIZADAS
  const activeWorksCount = works.filter(work => work.deletedDate === null).length;
  const finalizedWorksCount = works.filter(work => work.deletedDate !== null).length;

  // RENDERIZADO MEMOIZADO
  const renderContent = useMemo(() => {
    if (loading && filteredWorks.length === 0) {
      return (
        <div className="text-center p-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="mt-2">Cargando obras...</p>
        </div>
      );
    }
    
    return (
      <TableWrapper
        columnDefs={columnDefs}
        rowData={filteredWorks}
        tableType="obras"
        onGridReady={(e) => setGridApi(e.api)}
        onView={handleViewWork}
        onEdit={handleEditWork}
        onDelete={handleDeleteWork}
        style={customTableStyle}
        customGridOptions={{
          pagination: true,
          paginationPageSize: 50,
          defaultColDef: {
            resizable: true,
            sortable: true,
            filter: true,
            floatingFilter: true
          }
        }}
      />
    );
  }, [loading, filteredWorks, columnDefs, handleViewWork, handleEditWork, handleDeleteWork, customTableStyle]);

  return (
    <Container fluid className="p-0">
      <Card className="rounded-card-custom" style={{ overflow: 'hidden', borderRadius: '8px' }}>
        <Card.Header className="bg-primary text-white text-center">
          <h5 className="m-0">
            Obras
            {/* ✅ INDICADORES DE ESTADO MEJORADOS */}
            <Badge bg="light" text="dark" className="ms-2">
              {showFinalized 
                ? `Todas (${works.length})` 
                : `Activas (${activeWorksCount})`
              }
            </Badge>
            {/* ✅ INDICADOR DE FILTROS APLICADOS */}
            {hasActiveFilters && (
              <Badge bg="warning" text="dark" className="ms-1">
                Filtros aplicados
              </Badge>
            )}
          </h5>
        </Card.Header>

        {/* División principal que contiene filtros, botones y tabla */}
        <div className="bg-white">
          {/* Fila de filtros y botones */}
          <div className="d-flex justify-content-between align-items-center p-2 bg-light border-bottom">
            {/* Filtros dropdown */}
            <div className="d-flex gap-2">
              <FilterSelect
                options={clientOptions}
                activeFilters={activeClients}
                onFiltersChange={handleClientChange}
                onOptionsUpdate={handleClientOptionsUpdate}
                title="Filtrar por cliente"
                icon={<FaBuilding />}
                filterKey="works-list-clients"
              />
              
              <FilterSelect
                options={locationOptions}
                activeFilters={activeLocations}
                onFiltersChange={handleLocationChange}
                onOptionsUpdate={handleLocationOptionsUpdate}
                title="Filtrar por ubicación"
                icon={<FaMapMarkerAlt />}
                filterKey="works-list-locations"
              />

              {/* ✅ Filtro de estado - solo visible cuando showFinalized está activo */}
              {showFinalized && (
                <FilterSelect
                  options={statusOptions}
                  activeFilters={activeStatuses}
                  onFiltersChange={handleStatusChange}
                  onOptionsUpdate={handleStatusOptionsUpdate}
                  title="Filtrar por estado"
                  icon={<FaToggleOn />}
                  filterKey="works-list-status"
                />
              )}
            </div>
            
            {/* Botones de acción */}
            <div className="d-flex gap-2">
              <ExportCsvButton gridApi={gridApi} moduleName="obras" />
              {/* ✅ BOTÓN CON INDICADOR VISUAL MEJORADO */}
              <Button 
                variant={showFinalized ? "warning" : "outline-secondary"}
                size="sm"
                onClick={handleToggleShowFinalized}
                className="d-flex align-items-center"
                disabled={loading}
              >
                {showFinalized ? (
                  <>
                    <FaEyeSlash className="me-1" /> Ocultar Finalizadas
                    {hasActiveFilters && <Badge bg="light" text="dark" className="ms-1">●</Badge>}
                  </>
                ) : (
                  <>
                    <FaEye className="me-1" /> Ver Finalizadas
                    {finalizedWorksCount > 0 && (
                      <Badge bg="secondary" className="ms-1">
                        {finalizedWorksCount}
                      </Badge>
                    )}
                  </>
                )}
              </Button>
              
              {/* Botón "Nueva Obra" */}
              <Button 
                variant="primary" 
                size="sm"
                onClick={handleAddWork}
                className="d-flex align-items-center"
              >
                <FaPlus className="me-1" /> Nueva Obra
              </Button>
            </div>
          </div>

          {/* Tabla de obras */}
          <Card.Body className="p-0">
            {renderContent}
          </Card.Body>
        </div>
      </Card>
      
      <CustomSnackbar
        open={snackbar.open}
        type={snackbar.type}
        message={snackbar.message}
        onClose={handleCloseSnackbar}
      />

      {/* Estilos CSS */}
      <style jsx="true">{`
        .rounded-card-custom .ag-root-wrapper {
          border-radius: 8px !important;
        }
        .rounded-card-custom .ag-header {
          border-top-left-radius: 8px !important;
          border-top-right-radius: 8px !important;
        }
        .rounded-card-custom .ag-paging-panel {
          border-bottom-left-radius: 8px !important;
          border-bottom-right-radius: 8px !important;
        }
      `}</style>
    </Container>
  );
}

export default WorksList;